import type { RefObject } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useEffectOnce, useHoverDirty } from "react-use";

import type {
  ProductId,
  ProductListGroupId,
} from "../../../constants/modelCompareData/types";
import { ButtonGroup } from "./components/ButtonGroup";
import { Product } from "./components/Product/Product";
import { groupProducts } from "./components/ProductGroups/groupProducts";
import { ProductGroups } from "./components/ProductGroups/ProductGroups";
import { ScrollButton } from "./components/ScrollButton/ScrollButton";
import * as S from "./ProductList.styles";
import type { ProductListProps } from "./ProductList.types";
import { useIsAtMaxScroll } from "./useIsAtMaxScroll";

const CHECK_FOR_SCROLL_TIMEOUT = 150;

export const ProductList = ({
  products,
  selectedProducts,
  setSelectedProducts,
  hasBeenObserved,
  selectedProductsLimit,
  initialActiveGroup,
  setExternalState,
  showProductGroups,
  isOverviewPage,
}: ProductListProps) => {
  const groupedGroups = useMemo(() => groupProducts(products), [products]);
  const moreThanOneGroupExists = groupedGroups.length > 1;

  const containerRef = useRef<HTMLDivElement>(null);
  const productListRef = useRef<HTMLDivElement>(null);

  const [buttonHasBeenClicked, setButtonHasBeenClicked] = useState(false);
  const [activeGroup, setActiveGroup] = useState(initialActiveGroup);
  const [playAnimation, setPlayAnimation] = useState(false);
  const isHovering = useHoverDirty(
    // Hack: Type issue with ref on useHoverDirty side - https://github.com/streamich/react-use/issues/2379
    containerRef as RefObject<HTMLDivElement>,
  );
  const [isDragging, setIsDragging] = useState(false);
  const [overflowExists, setOverflowExists] = useState(true);
  const [endPosition, setEndPosition] = useState<number>(0);

  useEffectOnce(() => {
    if (
      containerRef.current &&
      productListRef.current &&
      containerRef.current?.getBoundingClientRect().width <
        productListRef.current?.getBoundingClientRect().width
    ) {
      setOverflowExists(true);
    } else {
      setOverflowExists(false);
    }
  });

  // TODO: figure out how to remove this TODO. Currently, without this `useEffect` `activeGroup` isn't updating when toggling between different tables (`Section`s).
  // ModelCompare is sometimes used to toggle between sets of data of type Section. In this situation, we need the initial active group to be updated when the new data is passed to ModelCompare
  useEffect(() => {
    setActiveGroup(products[0]?.productListGroup?.id as ProductListGroupId);
  }, [products]);

  // #############################################################################
  // Scroll-Specific useEffects
  // #############################################################################

  // if there aren't any product list group buttons then the ending state of the animations need to exist on the initial render
  useEffect(() => {
    if (!showProductGroups && hasBeenObserved) {
      setPlayAnimation(true);
    }
  }, [hasBeenObserved, setPlayAnimation, showProductGroups]);

  // plays animation when no scroll has occurred on the initial observe
  useEffect(() => {
    const productListInitialPosition =
      productListRef.current?.getBoundingClientRect().left;

    let timeoutId: NodeJS.Timeout;

    if (hasBeenObserved || buttonHasBeenClicked) {
      timeoutId = setTimeout(() => {
        const currentPosition =
          productListRef.current?.getBoundingClientRect().left;
        if (productListInitialPosition === currentPosition) {
          return setPlayAnimation(true);
        }
      }, CHECK_FOR_SCROLL_TIMEOUT);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [hasBeenObserved, setPlayAnimation, buttonHasBeenClicked]);

  // #############################################################################
  // Drag Logic
  // #############################################################################
  const dragStartingPosition = useRef(0);
  const scrollAmount = useRef(0);

  const dragHandler = useCallback(
    (event: MouseEvent) => {
      scrollAmount.current = dragStartingPosition.current - event.clientX;
      containerRef.current?.scrollTo(endPosition + scrollAmount.current, 0);
    },
    [endPosition],
  );

  const mouseUpHandler = useCallback(() => {
    setIsDragging(false);
    setEndPosition(containerRef.current?.scrollLeft ?? 0);
    document.removeEventListener("mousemove", dragHandler);
    document.removeEventListener("mouseup", mouseUpHandler);
  }, [dragHandler]);

  const mouseDownHandler = useCallback(
    (event: React.MouseEvent<Element, MouseEvent>) => {
      if (event.buttons === 1) {
        dragStartingPosition.current = event.clientX;
        setIsDragging(true);
        document.addEventListener("mousemove", dragHandler);
        document.addEventListener("mouseup", mouseUpHandler);
      }
    },
    [dragHandler, mouseUpHandler],
  );

  useEffect(() => {
    const container = containerRef.current;

    if (!container) return;

    let isScrolling: NodeJS.Timeout;

    const checkIsScrolling = () => {
      window.clearTimeout(isScrolling);

      // if scrolling occurs, animations are played after scrolling has completed
      // if scrolling occurs, the container's end position is tracked in state after scrolling has completed
      isScrolling = setTimeout(function () {
        setPlayAnimation(true);
        setEndPosition(containerRef.current?.scrollLeft as number);
      }, CHECK_FOR_SCROLL_TIMEOUT);
    };

    (containerRef.current as HTMLDivElement).addEventListener(
      "scroll",
      checkIsScrolling,
    );

    return () => {
      window.removeEventListener("resize", checkIsScrolling);
    };
  }, []); // Empty dependency array since we want this to run once

  // #############################################################################
  // Click to Scroll Logic
  // #############################################################################

  const tabClickHandler = useCallback(
    ({ groupId }: { groupId: ProductListGroupId }) => {
      const initialScrollPosition = (containerRef.current as HTMLDivElement)
        .scrollLeft;

      const checkForScroll = () => {
        const currentScrollPosition = (containerRef.current as HTMLDivElement)
          .scrollLeft;

        return currentScrollPosition === initialScrollPosition;
      };

      const element = document.getElementById(groupId) as HTMLDivElement;

      setPlayAnimation(false);
      setActiveGroup(groupId);
      setButtonHasBeenClicked(true);
      element?.scrollIntoView({
        behavior: "smooth",
        inline: "start",
        block: "nearest",
      });

      // if no scroll occurs animations are played
      setTimeout(() => {
        if (checkForScroll()) {
          setPlayAnimation(true);
        }
      }, CHECK_FOR_SCROLL_TIMEOUT);
    },
    [containerRef, setPlayAnimation, setActiveGroup],
  );

  const scrollButtonHandler = ({ isLeftArrow }: { isLeftArrow?: boolean }) => {
    if (containerRef.current) {
      const currentScrollPosition = containerRef.current?.scrollLeft;

      const scrollLength = isLeftArrow
        ? currentScrollPosition - S.PRODUCT_CARD_WIDTH - S.PRODUCT_LIST_GRID_GAP
        : currentScrollPosition +
          S.PRODUCT_CARD_WIDTH +
          S.PRODUCT_LIST_GRID_GAP;

      containerRef.current?.scrollTo(scrollLength, 0);
    }
  };

  const { isMaxLeft, isMaxRight } = useIsAtMaxScroll(containerRef);

  return (
    <S.Wrapper>
      {showProductGroups && moreThanOneGroupExists && (
        <ButtonGroup
          items={groupedGroups.map((group) => group.productListGroup)}
          activeButton={activeGroup as string}
          onClick={(id) => {
            tabClickHandler({ groupId: id as ProductListGroupId });
          }}
        />
      )}
      <S.Container
        ref={containerRef}
        $isHovering={isHovering}
        $moreThanOneGroupExists={moreThanOneGroupExists}
        $isDragging={isDragging}
      >
        <S.ProductGroupProductListContainer>
          {moreThanOneGroupExists && (
            <ProductGroups
              products={products}
              activeGroup={activeGroup as ProductListGroupId}
              playAnimation={playAnimation}
              canPlayAniimation={hasBeenObserved || buttonHasBeenClicked}
              showProductGroups={showProductGroups}
            />
          )}
          <S.ProductList
            $columns={products.length}
            $isDragging={isDragging}
            $overflowExists={overflowExists}
            $moreThanOneGroupExists={moreThanOneGroupExists}
            onMouseDown={(event) => mouseDownHandler(event)}
            onMouseUp={mouseUpHandler}
            ref={productListRef}
          >
            <ScrollButton
              color="white"
              rotationDegrees={180}
              isVisible={!isMaxLeft}
              onClick={() => scrollButtonHandler({ isLeftArrow: true })}
            />
            <ScrollButton
              color="white"
              rotationDegrees={0}
              isVisible={isMaxRight}
              isRightButton
              onClick={() => scrollButtonHandler({ isLeftArrow: false })}
            />
            {products.map((product, productsArrayIndex) => {
              const groupIsActive =
                product.productListGroup?.id === activeGroup;

              const productListGroup = products.filter(
                (product) => activeGroup === product.productListGroup?.id,
              );

              const groupIndex = productListGroup.findIndex(
                (item) => item.id === product.id,
              );

              return (
                <Product
                  onClick={() =>
                    setActiveGroup(
                      product.productListGroup?.id as ProductListGroupId,
                    )
                  }
                  key={product.id}
                  groupIndex={groupIndex}
                  groupLength={productListGroup.length}
                  id={product?.id as ProductId}
                  groupId={product.productListGroup?.id as ProductListGroupId}
                  image={product.image ?? { src: "", width: 0, height: 0 }}
                  name={product?.productTitle}
                  description={product.description}
                  selectedProducts={selectedProducts}
                  setSelectedProducts={setSelectedProducts}
                  setExternalState={setExternalState}
                  groupIsActive={groupIsActive}
                  playAnimation={playAnimation}
                  canPlayAniimation={hasBeenObserved || buttonHasBeenClicked}
                  selectedProductsLimit={selectedProductsLimit}
                  showProductGroups={showProductGroups}
                  isStaticProduct={productsArrayIndex === 0}
                  isOverviewPage={isOverviewPage}
                />
              );
            })}
          </S.ProductList>
        </S.ProductGroupProductListContainer>
      </S.Container>
    </S.Wrapper>
  );
};
